import { useState } from 'react';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import {
    AudioLines,
    Bot,
    Building2,
    FileText,
    Frame,
    GitBranch,
    Globe,
    Image,
    Layers,
    Plus,
    Search,
    User,
} from 'lucide-react';
import { toast } from 'sonner';
import { RenderIf } from '@/components/RenderIf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useCreateCanvas from '@/hooks/canvas/useCreateCanvas';
import getCanvasErrorMessage from './helpers/getCanvasErrorMessage';
import useCanvasOrganizationId from './hooks/useCanvasOrganizationId';
import CanvasFilterDropdown from './FilterDropdown';
import CanvasMultiFilterDropdown from './MultiFilterDropdown';
import type { CanvasFilterOption, CanvasOutletContext } from './types';
import {
    CanvasPageContainer,
    CanvasPageHeader,
    ContentContainer,
    HeaderActions,
    SearchContainer,
    SearchIcon,
} from './styled';

// The first option in each list is the neutral default (clears the param).
const OWNERSHIP_FILTER_OPTIONS: CanvasFilterOption[] = [
    { value: 'all', label: 'All', icon: Frame },
    { value: 'mine', label: 'Created by me', icon: User },
    { value: 'shared', label: 'Shared with me', icon: Building2 },
    { value: 'global', label: 'Global', icon: Globe },
];

const ORIGIN_FILTER_OPTIONS: CanvasFilterOption[] = [
    { value: '', label: 'Any source', icon: GitBranch },
    { value: 'user', label: 'Created by people', icon: User },
    { value: 'thread', label: 'From agent threads', icon: Bot },
];

// Media is multi-select, so there is no neutral default entry — an empty
// selection is itself the "any media" state.
const MEDIA_FILTER_OPTIONS: CanvasFilterOption[] = [
    { value: 'image', label: 'Images', icon: Image },
    { value: 'audio', label: 'Audio', icon: AudioLines },
    { value: 'markdown', label: 'Markdown', icon: FileText },
];

function CanvasPage() {
    const { agentId } = useParams<{ agentId: string }>();
    const navigate = useNavigate();
    const { organizationId } = useCanvasOrganizationId();
    const { createCanvas, isCreating } = useCreateCanvas({ organizationId });
    const [searchQuery, setSearchQuery] = useState('');
    const isOnListPage = useMatch('/agents/:agentId/canvas');

    const handleCreateCanvas = async () => {
        try {
            const created = await createCanvas({ name: 'Untitled canvas' });
            navigate(`/agents/${agentId}/canvas/${created.canvasId}`);
        } catch (error) {
            toast.error(
                getCanvasErrorMessage({
                    error,
                    fallback: 'Could not create canvas. Please try again.',
                })
            );
        }
    };

    const outletContext: CanvasOutletContext = { searchQuery };

    return (
        <CanvasPageContainer>
            <RenderIf isTrue={!!isOnListPage}>
                <CanvasPageHeader>
                    <HeaderActions>
                        <CanvasFilterDropdown
                            paramKey="filter"
                            label="Ownership"
                            options={OWNERSHIP_FILTER_OPTIONS}
                        />
                        <CanvasFilterDropdown
                            paramKey="origin"
                            label="Source"
                            options={ORIGIN_FILTER_OPTIONS}
                        />
                        <CanvasMultiFilterDropdown
                            paramKey="kind"
                            label="Media"
                            icon={Layers}
                            options={MEDIA_FILTER_OPTIONS}
                        />
                        <SearchContainer>
                            <SearchIcon>
                                <Search size={18} />
                            </SearchIcon>
                            <Input
                                type="text"
                                placeholder="Search canvases..."
                                value={searchQuery}
                                onChange={event => setSearchQuery(event.target.value)}
                            />
                        </SearchContainer>
                        <Button onClick={handleCreateCanvas} disabled={isCreating}>
                            <Plus className="mr-2 h-4 w-4" />
                            {isCreating ? 'Creating…' : 'New Canvas'}
                        </Button>
                    </HeaderActions>
                </CanvasPageHeader>
            </RenderIf>
            <ContentContainer>
                <Outlet context={outletContext} />
            </ContentContainer>
        </CanvasPageContainer>
    );
}

export default CanvasPage;
